import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { organizationService } from '../../services/organizationService';
import { paymentsService } from '../../services/paymentsService';
import { countryService } from '../../services/countryService';
import settingsService from '../../services/settingsService';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import Pagination from '../common/Pagination';
import { orgTimestamp, formatOrgDate } from '../../utils/dateUtils';
import Toast from '../common/Toast';
import { usePageTitle } from '../../hooks/usePageTitle';
import './OrganizationManagement.css';

const OrganizationManagement = () => {
    usePageTitle('Organization Management');
    const { user } = useAuth();
    const location = useLocation();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({});
    const [activeAlertFilter, setActiveAlertFilter] = useState(location.state?.filter || null);

    // Filters and search
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Categories and other dropdowns
    const [categories, setCategories] = useState([]);
    const [premisesTypes, setPremisesTypes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedOrgCategory, setSelectedOrgCategory] = useState('');
    const [selectedCountryId, setSelectedCountryId] = useState('');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showPremisesModal, setShowPremisesModal] = useState(false);
    const [showCreatePremisesModal, setShowCreatePremisesModal] = useState(false);
    const [showEditPremisesModal, setShowEditPremisesModal] = useState(false);
    const [showLogoModal, setShowLogoModal] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedPremises, setSelectedPremises] = useState(null);

    const [showBulkPremisesModal, setShowBulkPremisesModal] = useState(false);
    const [bulkPremisesLoading, setBulkPremisesLoading] = useState(false);
    const [bulkPremisesFile, setBulkPremisesFile] = useState(null);
    const [bulkPremisesResult, setBulkPremisesResult] = useState(null);
    const [bulkPremisesTypes, setBulkPremisesTypes] = useState([]);
    const [logoFiles, setLogoFiles] = useState([]);
    const [uploadMethod, setUploadMethod] = useState('separate'); // 'separate' or 'zip'
    const [bulkCities, setBulkCities] = useState([]);
    const [selectedCountryForBulk, setSelectedCountryForBulk] = useState('');
    const [showCitiesTable, setShowCitiesTable] = useState(false);
    const [showBulkStaffModal, setShowBulkStaffModal] = useState(false);
    const [bulkStaffLoading, setBulkStaffLoading] = useState(false);
    const [bulkStaffFile, setBulkStaffFile] = useState(null);
    const [bulkStaffResult, setBulkStaffResult] = useState(null);
    const [bulkDesignations, setBulkDesignations] = useState([]);
    const [bulkStaffImages, setBulkStaffImages] = useState([]);
    const [bulkStaffUploadMethod, setBulkStaffUploadMethod] = useState('separate'); // 'separate' or 'zip'
    // Organization premises data
    const [orgPremises, setOrgPremises] = useState([]);
    const [premisesLoading, setPremisesLoading] = useState(false);
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        const baseUrl = process.env.REACT_APP_API_URL;
        return imagePath.startsWith('http') ? imagePath : `${baseUrl}/${imagePath}`;
    };

    // Create form
    const [createForm, setCreateForm] = useState({
        name: '',
        admin_username: '',
        admin_email: '',
        admin_phone: '',
        country_id: '',
        org_category_id: '',
        description: ''
    });

    // Edit form
    const [editForm, setEditForm] = useState({
        id: '',
        name: '',
        admin_username: '',
        admin_email: '',
        admin_phone: '',
        org_category_id: '',
        description: '',
        contact_email: '',
        contact_phone: '',
        integration_enabled: 0,
        tax_value: '0.00',
        lock_offsite_radius_settings: 0
    });



    // Premises form
    const [premisesForm, setPremisesForm] = useState({
        org_id: '',
        premises_type_id: '',
        premises_name: '',
        address: '',
        city_id: '',
        poc_name: '',
        phone_number: '',
        contact_email: '',
        description: '',
        weekly_off_days: [],
        geofence: {
            point1: { latitude: '', longitude: '' },
            point2: { latitude: '', longitude: '' },
            point3: { latitude: '', longitude: '' },
            point4: { latitude: '', longitude: '' }
        },
        notifications: {
            geofence_breach: true,
            late_arrival: true,
            early_departure: true,
            radius: 100
        },
        logo_base64: ''
    });

    // Logo form
    const [logoForm, setLogoForm] = useState({
        org_id: '',
        logo_base64: '',
        logo_preview: ''
    });

    // Loading states
    const [createLoading, setCreateLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [premisesCreateLoading, setPremisesCreateLoading] = useState(false);
    const [premisesEditLoading, setPremisesEditLoading] = useState(false);
    const [logoUpdateLoading, setLogoUpdateLoading] = useState(false);
    const [createErrors, setCreateErrors] = useState({});
    const [editErrors, setEditErrors] = useState({});
    const [premisesErrors, setPremisesErrors] = useState({});

    // Toast notifications
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Approval form
    const [approvalForm, setApprovalForm] = useState({
        action: '',
        comments: '',
        sales_agent_id: '1', // Default to Core Admin
        sales_agent_start_date: new Date().toISOString().split('T')[0],
        sales_agent_end_date: '',
        sales_agent_commission_type: 'percentage',
        sales_agent_commission_value: '0.00',
        integration_enabled: 0, // Default to disabled
        tax_value: '0.00' // Tax percentage to be added to subscription payments
    });
    const [salesAgents, setSalesAgents] = useState([]);
    const [assignAgentForm, setAssignAgentForm] = useState({
        organization_id: '',
        sales_agent_id: '1',
        sales_agent_start_date: new Date().toISOString().split('T')[0],
        sales_agent_end_date: '',
        sales_agent_commission_type: 'percentage',
        sales_agent_commission_value: '0.00'
    });

    // City search state
    const [citySearch, setCitySearch] = useState('');
    const [citiesLoading, setCitiesLoading] = useState(false);

    // Stable form handlers to prevent re-renders
    const handleCreateFormChange = useCallback((field, value) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleEditFormChange = useCallback((field, value) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handlePremisesFormChange = useCallback((field, value) => {
        setPremisesForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleGeofencePointChange = useCallback((pointNumber, field, value) => {
        setPremisesForm(prev => ({
            ...prev,
            geofence: {
                ...prev.geofence,
                [`point${pointNumber}`]: {
                    ...prev.geofence[`point${pointNumber}`],
                    [field]: value
                }
            }
        }));
    }, []);

    // Modal close handlers - stable references
    const handleCloseCreateModal = useCallback(() => {
        setShowCreateModal(false);
        setCreateErrors({});
        resetCreateForm();
    }, []);

    const handleCloseBulkStaffModal = useCallback(() => {
        setShowBulkStaffModal(false);
        setBulkStaffFile(null);
        setBulkStaffResult(null);
        setBulkDesignations([]);
        setBulkStaffImages([]);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setShowEditModal(false);
        setEditErrors({});
        resetEditForm();
    }, []);

    const handleCloseCreatePremisesModal = useCallback(() => {
        setShowCreatePremisesModal(false);
        setPremisesErrors({});
        resetPremisesForm();
    }, []);

    const handleCloseEditPremisesModal = useCallback(() => {
        setShowEditPremisesModal(false);
        setPremisesErrors({});
        resetPremisesForm();
        setSelectedPremises(null);
    }, []);

    const handleCloseLogoModal = useCallback(() => {
        setShowLogoModal(false);
        resetLogoForm();
    }, []);

    const handleClosePremisesModal = useCallback(() => {
        setShowPremisesModal(false);
        setSelectedOrg(null);
        setOrgPremises([]);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setShowDeleteModal(false);
        setSelectedOrg(null);
    }, []);

    const handleCloseApprovalModal = useCallback(() => {
        setShowApprovalModal(false);
        setSelectedOrg(null);
        setApprovalForm({
            action: '',
            comments: '',
            sales_agent_id: '1',
            sales_agent_start_date: new Date().toISOString().split('T')[0],
            sales_agent_end_date: '',
            sales_agent_commission_type: 'percentage',
            sales_agent_commission_value: '0.00',
            integration_enabled: 0
        });
    }, []);

    const handleCloseAssignAgentModal = useCallback(() => {
        setShowAssignAgentModal(false);
        setSelectedOrg(null);
        setAssignAgentForm({
            organization_id: '',
            sales_agent_id: '1',
            sales_agent_start_date: new Date().toISOString().split('T')[0],
            sales_agent_end_date: '',
            sales_agent_commission_type: 'percentage',
            sales_agent_commission_value: '0.00'
        });
    }, []);

    const handleCloseDetailsModal = useCallback(() => {
        setShowDetailsModal(false);
        setSelectedOrg(null);
    }, []);

    useEffect(() => {
        fetchOrganizations();
        fetchCategories();
        fetchPremisesTypes();
        fetchCountries();
        loadSalesAgents();
    }, [currentPage, search, statusFilter, subscriptionStatusFilter, categoryFilter, activeAlertFilter]);

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: currentPage,
                limit: 20,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(subscriptionStatusFilter && { subscription_status: subscriptionStatusFilter }),
                ...(categoryFilter && { category_id: categoryFilter }),
                ...(activeAlertFilter === 'payment_overdue' && { payment_overdue: '1' }),
                ...(activeAlertFilter === 'inactive_orgs' && { inactive_orgs: '1' }),
            };

            const data = await organizationService.getOrganizations(params);
            setOrganizations(data?.organizations ?? []);
            setPagination(data?.pagination ?? {});
        } catch (err) {
            setError(err.message);
            showToast('Failed to fetch organizations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await organizationService.getOrganizationCategories();
            setCategories(data.categories);
        } catch (err) {
            console.error('Failed to fetch categories:', err.message);
        }
    };

    const fetchPremisesTypes = async (orgCategoryId = '') => {
        try {
            const data = await organizationService.getPremisesTypes(orgCategoryId);
            setPremisesTypes(data.premises_types);
        } catch (err) {
            console.error('Failed to fetch premises types:', err.message);
            setPremisesTypes([]);
        }
    };

    const fetchCountries = async () => {
        try {
            const data = await organizationService.getCountriesWithDialCodes();
            setCountries(data.countries);
        } catch (err) {
            console.error('Failed to fetch countries:', err.message);
        }
    };

    const loadSalesAgents = async () => {
        try {
            const response = await settingsService.getSalesAgents({ page: 1, limit: 1000, status: 1 });
            setSalesAgents(response.sales_agents || []);
        } catch (err) {
            console.error('Failed to fetch sales agents:', err.message);
            setSalesAgents([]);
        }
    };

    const fetchCities = useCallback(async (countryId, searchTerm = '') => {
        if (!countryId) return;

        try {
            setCitiesLoading(true);
            const data = await organizationService.getCities(countryId, searchTerm);
            setCities(data.cities);
            return Promise.resolve();
        } catch (err) {
            console.error('Failed to fetch cities:', err.message);
            setCities([]);
            return Promise.reject(err);
        } finally {
            setCitiesLoading(false);
        }
    }, []);

    const fetchOrganizationPremises = async (orgId) => {
        try {
            setPremisesLoading(true);
            const data = await organizationService.getOrganizationPremises(orgId);
            setOrgPremises(data.premises || []);
        } catch (err) {
            showToast('Failed to fetch premises', 'error');
            setOrgPremises([]);
        } finally {
            setPremisesLoading(false);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setCreateErrors({});

        if (!createForm.country_id) {
            setCreateErrors({ country_id: 'Country is required' });
            setCreateLoading(false);
            return;
        }

        try {
            await organizationService.createOrganization(createForm);

            setShowCreateModal(false);
            resetCreateForm();
            fetchOrganizations();
            showToast('Organization created successfully! Welcome email sent to admin.', 'success');
        } catch (error) {
            if (error.message.includes('validation')) {
                setCreateErrors({ general: error.message });
            } else {
                showToast(error.message, 'error');
            }
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditErrors({});

        try {
            await organizationService.updateOrganization(editForm.id, editForm);

            setShowEditModal(false);
            resetEditForm();
            fetchOrganizations();
            showToast('Organization updated successfully!', 'success');
        } catch (error) {
            if (error.message.includes('validation')) {
                setEditErrors({ general: error.message });
            } else {
                showToast(error.message, 'error');
            }
        } finally {
            setEditLoading(false);
        }
    };

    const handleCreatePremises = async (e) => {
        e.preventDefault();
        setPremisesCreateLoading(true);
        setPremisesErrors({});

        try {
            const premisesData = {
                ...premisesForm,
                gps_coordinates1: `${premisesForm.geofence.point1.latitude},${premisesForm.geofence.point1.longitude}`,
                gps_coordinates2: `${premisesForm.geofence.point2.latitude},${premisesForm.geofence.point2.longitude}`,
                gps_coordinates3: `${premisesForm.geofence.point3.latitude},${premisesForm.geofence.point3.longitude}`,
                gps_coordinates4: `${premisesForm.geofence.point4.latitude},${premisesForm.geofence.point4.longitude}`,
                // Remove the geofence object since we're using individual coordinates
                geofence: undefined
            };

            await organizationService.createOrganizationPremises(premisesData);
            //await organizationService.createOrganizationPremises(premisesForm);

            setShowCreatePremisesModal(false);
            resetPremisesForm();
            await fetchOrganizationPremises(selectedOrg.id);
            showToast('Premises created successfully!', 'success');
        } catch (error) {
            if (error.message.includes('validation')) {
                setPremisesErrors({ general: error.message });
            } else {
                showToast(error.message, 'error');
            }
        } finally {
            setPremisesCreateLoading(false);
        }
    };

    const handleEditPremises = async (e) => {
        e.preventDefault();
        setPremisesEditLoading(true);
        setPremisesErrors({});

        try {
            const premisesData = {
                ...premisesForm,
                gps_coordinates1: `${premisesForm.geofence.point1.latitude},${premisesForm.geofence.point1.longitude}`,
                gps_coordinates2: `${premisesForm.geofence.point2.latitude},${premisesForm.geofence.point2.longitude}`,
                gps_coordinates3: `${premisesForm.geofence.point3.latitude},${premisesForm.geofence.point3.longitude}`,
                gps_coordinates4: `${premisesForm.geofence.point4.latitude},${premisesForm.geofence.point4.longitude}`,
                // Remove the geofence object since we're using individual coordinates
                geofence: undefined
            };

            //await organizationService.createOrganizationPremises(premisesData);
            await organizationService.updateOrganizationPremises(selectedPremises.id, premisesData);

            setShowEditPremisesModal(false);
            resetPremisesForm();
            setSelectedPremises(null);
            await fetchOrganizationPremises(selectedOrg.id);
            showToast('Premises updated successfully!', 'success');
        } catch (error) {
            if (error.message.includes('validation')) {
                setPremisesErrors({ general: error.message });
            } else {
                showToast(error.message, 'error');
            }
        } finally {
            setPremisesEditLoading(false);
        }
    };

    const handleDeletePremises = async (premises) => {
        if (!window.confirm(`Are you sure you want to delete "${premises.premises_name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await organizationService.deleteOrganizationPremises(premises.id);
            await fetchOrganizationPremises(selectedOrg.id);
            showToast('Premises deleted successfully!', 'success');
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const handleLogoUpdate = async (e) => {
        e.preventDefault();
        setLogoUpdateLoading(true);

        try {
            await organizationService.updateOrganizationLogo(logoForm.org_id, logoForm.logo_base64);

            setShowLogoModal(false);
            resetLogoForm();
            fetchOrganizations();
            showToast('Organization logo updated successfully!', 'success');
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLogoUpdateLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedOrg) return;

        setDeleteLoading(true);
        try {
            await organizationService.deleteOrganization(selectedOrg.id);

            setShowDeleteModal(false);
            setSelectedOrg(null);
            fetchOrganizations();
            showToast('Organization deleted successfully!', 'success');
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleApproval = async (e) => {
        e.preventDefault();
        if (!selectedOrg || !approvalForm.action) return;

        try {
            // Prepare sales agent data if approving
            const salesAgentData = approvalForm.action === 'approve' ? {
                sales_agent_id: approvalForm.sales_agent_id,
                sales_agent_start_date: approvalForm.sales_agent_start_date,
                sales_agent_end_date: approvalForm.sales_agent_end_date || null,
                sales_agent_commission_type: approvalForm.sales_agent_commission_type,
                sales_agent_commission_value: parseFloat(approvalForm.sales_agent_commission_value) || 0.00,
                integration_enabled: approvalForm.integration_enabled,
                tax_value: parseFloat(approvalForm.tax_value) || 0.00
            } : {};

            await organizationService.approveOrganization(
                selectedOrg.id,
                approvalForm.action,
                approvalForm.comments,
                salesAgentData
            );

            setShowApprovalModal(false);
            setSelectedOrg(null);
            setApprovalForm({
                action: '',
                comments: '',
                sales_agent_id: '1',
                sales_agent_start_date: new Date().toISOString().split('T')[0],
                sales_agent_end_date: '',
                sales_agent_commission_type: 'percentage',
                sales_agent_commission_value: '0.00',
                integration_enabled: 0,
                tax_value: '0.00'
            });
            fetchOrganizations();
            showToast(
                `Organization ${approvalForm.action}d successfully! Email notification sent to admin.`,
                'success'
            );
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const openAssignAgentModal = (org) => {
        setSelectedOrg(org);
        setAssignAgentForm({
            organization_id: String(org.id),
            sales_agent_id: String(org.sales_agent_id || 1),
            sales_agent_start_date: org.sales_agent_start_date
                ? String(org.sales_agent_start_date).split(' ')[0]
                : new Date().toISOString().split('T')[0],
            sales_agent_end_date: org.sales_agent_end_date
                ? String(org.sales_agent_end_date).split(' ')[0]
                : '',
            sales_agent_commission_type: org.sales_agent_commission_type || 'percentage',
            sales_agent_commission_value: org.sales_agent_commission_value || '0.00'
        });
        setShowAssignAgentModal(true);
    };

    const handleAssignAgent = async (e) => {
        e.preventDefault();
        if (!assignAgentForm.organization_id || !assignAgentForm.sales_agent_id) {
            showToast('Organization and sales agent are required', 'error');
            return;
        }

        try {
            await settingsService.assignSalesAgentToOrganization({
                organization_id: Number(assignAgentForm.organization_id),
                sales_agent_id: Number(assignAgentForm.sales_agent_id),
                sales_agent_start_date: assignAgentForm.sales_agent_start_date || undefined,
                sales_agent_end_date: assignAgentForm.sales_agent_end_date || undefined,
                sales_agent_commission_type: assignAgentForm.sales_agent_commission_type,
                sales_agent_commission_value: Number(assignAgentForm.sales_agent_commission_value || 0)
            });
            showToast('Sales agent reassigned successfully', 'success');
            handleCloseAssignAgentModal();
            fetchOrganizations();
        } catch (error) {
            showToast(error.message || 'Failed to reassign sales agent', 'error');
        }
    };

    const handlePremisesStatusChange = async (premisesId, newStatus) => {
        try {
            await organizationService.updatePremisesStatus(premisesId, newStatus);
            await fetchOrganizationPremises(selectedOrg.id);

            const statusText = {
                0: 'Inactive',
                1: 'Active',
                2: 'Demo',
                3: 'Special'
            };

            showToast(`Premises status updated to ${statusText[newStatus]}!`, 'success');
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const handleStatusToggle = async (org, newStatus) => {
        try {
            // If approving (status 2) or rejecting (status 0), use the approveOrganization endpoint
            // which sends email notifications
            if (newStatus === 2 || newStatus === 0) {
                const action = newStatus === 2 ? 'approve' : 'reject';
                await organizationService.approveOrganization(org.id, action, '');
            } else {
                // For other status changes (suspend/activate), use toggleOrganizationStatus
                await organizationService.toggleOrganizationStatus(org.id, newStatus);
            }

            fetchOrganizations();
            const statusText = {
                0: 'rejected',
                1: 'pending approval',
                2: 'approved',
                3: 'suspended'
            };
            showToast(`Organization ${statusText[newStatus]} successfully!`, 'success');
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    const handleImageUpload = async (file, target) => {
        try {
            const base64 = await organizationService.fileToBase64(file);

            if (target === 'organization') {
                setLogoForm(prev => ({
                    ...prev,
                    logo_base64: base64,
                    logo_preview: base64
                }));
            } else if (target === 'premises') {
                setPremisesForm(prev => ({
                    ...prev,
                    logo_base64: base64
                }));
            }
        } catch (error) {
            showToast('Failed to process image', 'error');
        }
    };

    const openEditModal = useCallback(async (org) => {
        try {
            const details = await organizationService.getOrganizationById(org.id);
            setEditForm({
                id: org.id,
                name: org.name,
                admin_username: details.admin_username || '',
                admin_email: details.admin_email || '',
                admin_phone: details.admin_phone || '',
                org_category_id: org.org_category_id || '',
                description: org.description || '',
                contact_email: org.contact_email || '',
                contact_phone: org.contact_phone || '',
                integration_enabled: org.integration_enabled || 0,
                tax_value: org.tax_value || '0.00',
                lock_offsite_radius_settings: details.lock_offsite_radius_settings || 0
            });
            setShowEditModal(true);
        } catch (error) {
            showToast('Failed to load organization details', 'error');
        }
    }, []);

    const openPremisesModal = async (org) => {
        setSelectedOrg(org);
        setShowPremisesModal(true);
        await fetchOrganizationPremises(org.id);
    };

    const openCreatePremisesModal = async (org) => {
        setSelectedOrg(org);
        setSelectedOrgCategory(org.org_category_id || '');
        setPremisesForm(prev => ({ ...prev, org_id: org.id }));
        setShowCreatePremisesModal(true);

        // Fetch premises types based on organization category
        if (org.org_category_id) {
            fetchPremisesTypes(org.org_category_id);
        }

        // Fetch cities based on organization's country_code
        if (org.country_code) {
            try {
                setCitiesLoading(true);
                const data = await countryService.getCitiesByCountryCode(org.country_code);
                setCities(data.cities || []);
            } catch (err) {
                console.error('Error fetching cities for organization:', err);
                setCities([]);
                showToast('Failed to load cities for this organization.', 'error');
            } finally {
                setCitiesLoading(false);
            }
        } else {
            console.warn(`Organization ${org.id} does not have a country_code.`);
            setCities([]);
        }
    };

    const handleExportOrganizations = async () => {
        try {
            const filters = {
                search,
                status: statusFilter,
                category_id: categoryFilter
            };
            await organizationService.exportOrganizations(filters);
            showToast('Organizations exported successfully!', 'success');
        } catch (error) {
            showToast(error.message || 'Failed to export organizations', 'error');
        }
    };

    const openBulkStaffModal = async (org, premises) => {
        setSelectedOrg(org);
        setSelectedPremises(premises); // Set the specific premises
        setShowBulkStaffModal(true);

        // Reset states
        setBulkDesignations([]);
        setBulkStaffImages([]);
        setBulkStaffFile(null);
        setBulkStaffResult(null);

        // Fetch designations for the organization category and premises
        if (org.org_category_id) {
            try {
                const data = await organizationService.getDesignations(org.org_category_id, premises.id);
                setBulkDesignations(data.designations);
            } catch (error) {
                console.error('Failed to fetch designations:', error);
                setBulkDesignations([]);
            }
        }
    };

    const handleBulkStaffUpload = async (e) => {
        e.preventDefault();
        if (!bulkStaffFile || !selectedOrg || !selectedPremises) return;

        setBulkStaffLoading(true);

        try {
            const result = await organizationService.bulkUploadStaff(
                selectedOrg.id,
                selectedPremises.id, // Pass the specific premises ID
                bulkStaffFile,
                bulkStaffUploadMethod === 'separate' ? bulkStaffImages : [],
                bulkStaffUploadMethod
            );
            setBulkStaffResult(result);
            showToast(`Bulk staff upload completed! ${result.success_count} staff created for ${selectedPremises.premises_name}`, 'success');
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setBulkStaffLoading(false);
        }
    };

    const openBulkPremisesModal = async (org) => {
        setSelectedOrg(org);
        setShowBulkPremisesModal(true);

        // Reset states
        setCitySearch('');

        // Fetch premises types for the organization category
        if (org.org_category_id) {
            try {
                const data = await organizationService.getPremisesTypes(org.org_category_id);
                setBulkPremisesTypes(data.premises_types);
            } catch (error) {
                console.error('Failed to fetch premises types:', error);
                setBulkPremisesTypes([]);
            }
        } else {
            setBulkPremisesTypes([]);
        }

        // Fetch cities based on organization's country_code
        if (org.country_code) {
            try {
                setCitiesLoading(true);
                const data = await countryService.getCitiesByCountryCode(org.country_code);
                setBulkCities(data.cities || []);
                setShowCitiesTable(true); // Auto-show the cities table
            } catch (err) {
                console.error('Error fetching cities for bulk upload:', err);
                setBulkCities([]);
                setShowCitiesTable(false);
                showToast('Failed to load cities for reference.', 'error');
            } finally {
                setCitiesLoading(false);
            }
        } else {
            console.warn(`Organization ${org.id} does not have a country_code.`);
            setBulkCities([]);
            setShowCitiesTable(false);
            showToast('Organization does not have a country code set. Cannot load cities.', 'warning');
        }
    };

    const fetchCitiesForBulk = async (countryId, searchTerm = '') => {
        if (!countryId) return;

        setCitiesLoading(true);
        try {
            const data = await organizationService.getCities(countryId, searchTerm);
            setBulkCities(data.cities);
        } catch (error) {
            console.error('Failed to fetch cities:', error);
            setBulkCities([]);
        } finally {
            setCitiesLoading(false);
        }
    };

    const handleCountryChangeForBulk = (countryId) => {
        setSelectedCountryForBulk(countryId);
        setCitySearch('');
        if (countryId) {
            fetchCitiesForBulk(countryId);
            setShowCitiesTable(true);
        } else {
            setBulkCities([]);
            setShowCitiesTable(false);
        }
    };

    const handleCitySearchForBulk = async (searchTerm) => {
        setCitySearch(searchTerm);

        // Only search if we have an organization with country_code
        if (!selectedOrg || !selectedOrg.country_code) return;

        if (searchTerm.length >= 2) {
            try {
                setCitiesLoading(true);
                const data = await countryService.getCitiesByCountryCode(selectedOrg.country_code, searchTerm);
                setBulkCities(data.cities || []);
            } catch (err) {
                console.error('Error searching cities:', err);
                setBulkCities([]);
            } finally {
                setCitiesLoading(false);
            }
        } else if (searchTerm.length === 0) {
            // Reset to all cities when search is cleared
            try {
                setCitiesLoading(true);
                const data = await countryService.getCitiesByCountryCode(selectedOrg.country_code);
                setBulkCities(data.cities || []);
            } catch (err) {
                console.error('Error fetching cities:', err);
                setBulkCities([]);
            } finally {
                setCitiesLoading(false);
            }
        }
    };



    const downloadZipTemplate = async (premisesTypes) => {
        // For ZIP template, we'll create a downloadable instruction file
        // since we can't create actual ZIP files in the browser easily
        const csvContent = generateCSVContent(premisesTypes);
        const instructionsContent = `
ZIP Package Template Instructions
================================

1. Create a ZIP file with the following structure:

premises-upload.zip
├── premises.csv (required - see template below)
├── office_logo.jpg (optional - logo files)
├── branch_logo.png (optional - logo files)

2. The CSV file should be named exactly "premises.csv"

3. Logo files can be in the root of the ZIP or in a "logos/" subfolder

4. Logo filenames in the CSV should match the actual files in the ZIP

5. Supported image formats: JPG, JPEG, PNG, GIF

Template CSV Content:
--------------------
${csvContent}

Valid Premises Types for ${selectedOrg?.category_name}:
${premisesTypes.map(type => `ID: ${type.id} = ${type.premisese_type_name}`).join('\n')}
`;

        const blob = new Blob([instructionsContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `zip_template_instructions_${selectedOrg?.name?.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const escapeCSVValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);

        // If the value contains comma, newline, or double quote, wrap in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            return '"' + stringValue.replace(/"/g, '""') + '"';
        }
        return stringValue;
    };


    const generateCSVContent = (premisesTypes) => {
        const headers = [
            'premises_name', 'address', 'premises_type_id', 'city_id', 'poc_name',
            'phone_number', 'contact_email', 'description', 'weekly_off_days',
            'gps_coordinates1', 'gps_coordinates2', 'gps_coordinates3',
            'gps_coordinates4', 'logo_filename'
        ];

        const sampleRows = [
            [
                'Main Office',
                '"123 Business Street, Block A, Victoria Island, Lagos"', // Properly quoted address with commas
                premisesTypes[0]?.id || '1',
                '', // Empty city_id
                'John Doe',
                '+1234567890',
                'main@company.com',
                '"Main headquarters building, ground floor reception"', // Quoted description with commas
                'Saturday,Sunday',
                '"6.5244,3.3792"', // Quoted GPS coordinates
                '"6.5245,3.3793"',
                '"6.5246,3.3794"',
                '"6.5247,3.3795"',
                'office_logo.jpg'
            ],
            [
                'Branch Office',
                '"456 Branch Avenue, Central Area, Abuja, FCT"', // Properly quoted address
                premisesTypes[1]?.id || premisesTypes[0]?.id || '1',
                '',
                'Jane Smith',
                '+1234567891',
                'branch@company.com',
                '"Branch location, second floor office space"', // Quoted description
                'Sunday',
                '"9.0579,7.4951"', // Quoted GPS coordinates for Abuja
                '"9.0580,7.4952"',
                '"9.0581,7.4953"',
                '"9.0582,7.4954"',
                'branch_logo.png'
            ]
        ];

        let csvContent = headers.join(',') + '\n';

        sampleRows.forEach(row => {
            // Process each value to ensure proper CSV escaping
            const escapedRow = row.map(value => escapeCSVValue(value));
            csvContent += escapedRow.join(',') + '\n';
        });

        return csvContent;
    };

    const downloadStaffCSVTemplate = (designations, selectedPremises) => {
        const headers = [
            'name', 'staff_id', 'designation', 'phone_number',
            'country_code', 'email', 'username', 'gender', 'dob',
            'date_of_appointment', 'image_filename'
        ];

        const sampleRows = [
            [
                'John Doe',
                'STF001',
                designations[0]?.designation_name || 'Manager',
                '8012345678',
                '+234',
                'john@company.com',
                'johndoe',
                'Male',
                '1990-05-15',
                '2024-01-15',
                'john_photo.jpg'
            ],
            [
                'Jane Smith',
                'STF002',
                designations[1]?.designation_name || 'Assistant',
                '8087654321',
                '+234',
                'jane@company.com',
                'janesmith',
                'Female',
                '1992-08-20',
                '2024-02-01',
                'jane_photo.jpg'
            ]
        ];

        let csvContent = headers.join(',') + '\n';
        csvContent += `# Staff upload for: ${selectedPremises?.premises_name}\n`;
        csvContent += `# Premises ID: ${selectedPremises?.id} (automatically assigned)\n`;
        csvContent += '# Available Designations:\n';
        designations.forEach(designation => {
            csvContent += `# - ${designation.designation_name}\n`;
        });
        csvContent += '# Default password for all staff: eHAAZRI\n';
        csvContent += '# Remove these comment lines before uploading\n';

        sampleRows.forEach(row => {
            csvContent += row.map(value => `"${value}"`).join(',') + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `staff_template_${selectedPremises?.premises_name?.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const downloadCSVTemplate = (premisesTypes) => {
        const csvContent = generateCSVContent(premisesTypes);

        // Create instruction content
        const instructionsContent = `CSV Template Instructions
=========================

IMPORTANT: CSV Format Guidelines
--------------------------------

1. ADDRESSES: Wrap addresses containing commas in double quotes
   Example: "123 Main Street, Suite 100, City Name"

2. GPS COORDINATES: Wrap latitude,longitude pairs in double quotes
   Example: "6.5244,3.3792"

3. DESCRIPTIONS: Wrap descriptions with commas in double quotes
   Example: "Main office building, ground floor, reception area"

4. If a field contains double quotes, escape them by doubling them
   Example: "He said ""Hello"" to me" for: He said "Hello" to me

5. Empty fields can be left blank (no quotes needed)

Valid Premises Type IDs for ${selectedOrg?.category_name}:
${premisesTypes.map(type => `ID: ${type.id} = ${type.premisese_type_name}`).join('\n')}

CSV Template:
============
${csvContent}

GPS Coordinates Format:
- Use the format "latitude,longitude" wrapped in quotes
- Example: "6.5244,3.3792"
- Get coordinates from Google Maps or similar service

Address Format:
- Include full address with commas as needed
- Wrap the entire address in quotes
- Example: "123 Business Street, Block A, Victoria Island, Lagos"
`;

        const blob = new Blob([instructionsContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `premises_template_with_instructions_${selectedOrg?.name?.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        // Also download just the CSV file
        const csvBlob = new Blob([csvContent], { type: 'text/csv' });
        const csvUrl = window.URL.createObjectURL(csvBlob);
        const csvLink = document.createElement('a');
        csvLink.href = csvUrl;
        csvLink.download = `premises_template_${selectedOrg?.name?.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
        document.body.appendChild(csvLink);
        csvLink.click();
        document.body.removeChild(csvLink);
        window.URL.revokeObjectURL(csvUrl);
    };

    const handleBulkPremisesUpload = async (e) => {
        e.preventDefault();
        if (!bulkPremisesFile || !selectedOrg) return;

        setBulkPremisesLoading(true);

        try {
            const result = await organizationService.bulkUploadPremises(
                selectedOrg.id,
                bulkPremisesFile,
                uploadMethod === 'separate' ? logoFiles : [],
                uploadMethod
            );
            setBulkPremisesResult(result);
            showToast(`Bulk upload completed! ${result.success_count} premises created, ${result.error_count} errors`, 'success');

            if (showPremisesModal) {
                await fetchOrganizationPremises(selectedOrg.id);
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setBulkPremisesLoading(false);
        }
    };

    const openEditPremisesModal = async (premises) => {
        setSelectedPremises(premises);

        // Set form data
        setPremisesForm({
            org_id: premises.org_id || selectedOrg.id,
            premises_type_id: premises.premises_type_id || '',
            premises_name: premises.premises_name || '',
            address: premises.address || '',
            city_id: premises.city_id || '',
            poc_name: premises.poc_name || '',
            phone_number: premises.phone_number || '',
            contact_email: premises.contact_email || '',
            description: premises.description || '',
            weekly_off_days: premises.weekly_off_days ? premises.weekly_off_days.split(',') : [],
            geofence: {
                point1: {
                    latitude: premises.gps_cordinates1?.split(',')[0] || '',
                    longitude: premises.gps_cordinates1?.split(',')[1] || ''
                },
                point2: {
                    latitude: premises.gps_cordinates2?.split(',')[0] || '',
                    longitude: premises.gps_cordinates2?.split(',')[1] || ''
                },
                point3: {
                    latitude: premises.gps_cordinates3?.split(',')[0] || '',
                    longitude: premises.gps_cordinates3?.split(',')[1] || ''
                },
                point4: {
                    latitude: premises.gps_cordinates4?.split(',')[0] || '',
                    longitude: premises.gps_cordinates4?.split(',')[1] || ''
                }
            },
            logo_base64: ''
        });

        // Load cities if country is available
        if (premises.city_id) {
            // Find the country for this city and load cities
            const city = cities.find(c => c.id === premises.city_id);
            if (city && city.country_id) {
                setSelectedCountryId(city.country_id);
                await fetchCities(city.country_id);
            }
        }

        setShowEditPremisesModal(true);
    };

    const openLogoModal = (org) => {
        setLogoForm({
            org_id: org.id,
            logo_base64: '',
            logo_preview: ''
        });
        setShowLogoModal(true);
    };

    const viewOrganizationDetails = useCallback(async (org) => {
        try {
            const details = await organizationService.getOrganizationById(org.id);
            setSelectedOrg({ ...org, ...details });
            setShowDetailsModal(true);
        } catch (error) {
            showToast('Failed to load organization details', 'error');
        }
    }, []);

    const resetCreateForm = () => {
        setCreateForm({
            name: '',
            admin_username: '',
            admin_email: '',
            admin_phone: '',
            country_id: '',
            org_category_id: '',
            description: ''
        });
    };

    const resetEditForm = () => {
        setEditForm({
            id: '',
            name: '',
            admin_username: '',
            admin_email: '',
            admin_phone: '',
            org_category_id: '',
            description: '',
            contact_email: '',
            contact_phone: '',
            integration_enabled: 0,
            tax_value: '0.00',
            lock_offsite_radius_settings: 0
        });
    };

    const handleCloseBulkPremisesModal = useCallback(() => {
        setShowBulkPremisesModal(false);
        setBulkPremisesFile(null);
        setBulkPremisesResult(null);
        setBulkCities([]);
        setSelectedCountryForBulk('');
        setCitySearch('');
        setShowCitiesTable(false);
    }, []);



    const resetPremisesForm = () => {
        setPremisesForm({
            org_id: '',
            premises_type_id: '',
            premises_name: '',
            address: '',
            city_id: '',
            poc_name: '',
            phone_number: '',
            contact_email: '',
            description: '',
            weekly_off_days: [],
            geofence: {
                point1: { latitude: '', longitude: '' },
                point2: { latitude: '', longitude: '' },
                point3: { latitude: '', longitude: '' },
                point4: { latitude: '', longitude: '' }
            },
            notifications: {
                geofence_breach: true,
                late_arrival: true,
                early_departure: true,
                radius: 100
            },
            logo_base64: ''
        });
    };

    const resetLogoForm = () => {
        setLogoForm({
            org_id: '',
            logo_base64: '',
            logo_preview: ''
        });
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const handleExtendSubscription = async () => {
        if (!selectedOrg?.id) {
            showToast('No organization selected', 'error');
            return;
        }

        const currentEndDate = selectedOrg?.subscription_end_date
            ? String(selectedOrg.subscription_end_date).split(' ')[0]
            : '';
        const endDateInput = window.prompt(
            'Enter new subscription end date (YYYY-MM-DD):',
            currentEndDate || ''
        );
        if (endDateInput === null) return;

        if (!/^\d{4}-\d{2}-\d{2}$/.test(endDateInput.trim())) {
            showToast('Date must be in YYYY-MM-DD format', 'error');
            return;
        }

        const reason = window.prompt('Reason for extension (optional):', '') || '';

        try {
            setLoading(true);
            const result = await paymentsService.extendSubscription(selectedOrg.id, endDateInput.trim(), reason);

            setSelectedOrg((prev) => prev ? ({
                ...prev,
                subscription_status: 'active',
                subscription_end_date: result.new_end_date
            }) : prev);

            await fetchOrganizations();
            showToast(`Subscription end date updated to ${new Date(result.new_end_date).toLocaleDateString()}`, 'success');
        } catch (error) {
            showToast(error.message || 'Failed to extend subscription', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setCurrentPage(1);
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const handleCategoryFilter = (value) => {
        setCategoryFilter(value);
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            0: { text: 'Rejected', class: 'status-rejected' },
            1: { text: 'Pending', class: 'status-pending' },
            2: { text: 'Approved', class: 'status-approved' },
            3: { text: 'Suspended', class: 'status-suspended' }
        };

        const config = statusConfig[status] || { text: 'Unknown', class: 'status-unknown' };

        return (
            <span className={`status-badge ${config.class}`}>
                {config.text}
            </span>
        );
    };

    const getSubscriptionBadge = (subscriptionStatus) => {
        if (!subscriptionStatus) return <span className="subscription-badge sub-none">No Subscription</span>;

        const statusConfig = {
            'active': { text: 'Active', class: 'sub-active' },
            'expired': { text: 'Expired', class: 'sub-expired' },
            'cancelled': { text: 'Cancelled', class: 'sub-cancelled' },
            'suspended': { text: 'Suspended', class: 'sub-suspended' },
            'trial': { text: 'Trial', class: 'sub-trial' }
        };

        const config = statusConfig[subscriptionStatus] || { text: 'Unknown', class: 'sub-unknown' };

        return (
            <span className={`subscription-badge ${config.class}`}>
                {config.text}
            </span>
        );
    };

    const getUsagePercentage = (current, max) => {
        if (!max || max === 0) return 0;
        return Math.min(100, (current / max) * 100);
    };

    const getUsedStaffCount = (org) => {
        return Number(org?.used_staff_count ?? org?.current_staff_count ?? 0);
    };

    const getTotalStaffCount = (org) => {
        return Number(org?.total_subscription_count ?? org?.max_staff_allowed ?? org?.current_license_count ?? 0);
    };

    if (user?.user_type !== 'core_admin') {
        return (
            <div className="access-denied">
                <h2>Access Denied</h2>
                <p>You don't have permission to access this page.</p>
            </div>
        );
    }

    return (
        <div className="organization-management">
            <div className="page-header">
                <div className="header-left">
                    <h1>
                        <i className="fas fa-building"></i>
                        Organization Management
                    </h1>
                    <p>Manage all organizations, approvals, and premises</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={handleExportOrganizations}
                        title="Export organizations as CSV"
                    >
                        <i className="fas fa-download"></i>
                        Export CSV
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <i className="fas fa-plus"></i>
                        Create Organization
                    </button>
                </div>
            </div>

            {/* Alert filter banner — shown when navigated from a dashboard alert */}
            {activeAlertFilter && (
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', background: activeAlertFilter === 'payment_overdue' ? 'rgba(231,76,60,0.08)' : 'rgba(108,117,125,0.08)', border: `1px solid ${activeAlertFilter === 'payment_overdue' ? '#e74c3c' : '#6c757d'}`, borderRadius:'8px', padding:'0.6rem 1rem', marginBottom:'0.75rem' }}>
                    <i className={`fas ${activeAlertFilter === 'payment_overdue' ? 'fa-exclamation-circle' : 'fa-pause-circle'}`} style={{ color: activeAlertFilter === 'payment_overdue' ? '#e74c3c' : '#6c757d' }}></i>
                    <span style={{ flex:1, fontSize:'0.9rem', fontWeight:600 }}>
                        {activeAlertFilter === 'payment_overdue' ? 'Showing organisations with overdue payments' : 'Showing inactive organisations'}
                    </span>
                    <button onClick={() => setActiveAlertFilter(null)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.85rem', color:'var(--text-light)', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                        <i className="fas fa-times"></i> Clear filter
                    </button>
                </div>
            )}

            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search organizations..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="search-input"
                    />
                    <i className="fas fa-search"></i>
                </div>

                <select
                    value={statusFilter}
                    onChange={(e) => { handleStatusFilter(e.target.value); setActiveAlertFilter(null); }}
                    className="filter-select"
                >
                    <option value="">All Status</option>
                    <option value="0">Rejected</option>
                    <option value="1">Pending</option>
                    <option value="2">Approved</option>
                    <option value="3">Suspended</option>
                </select>

                <select
                    value={subscriptionStatusFilter}
                    onChange={(e) => { setSubscriptionStatusFilter(e.target.value); setActiveAlertFilter(null); setCurrentPage(1); }}
                    className="filter-select"
                >
                    <option value="">All Subscriptions</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                </select>

                <select
                    value={categoryFilter}
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                        <option key={category.id} value={category.id}>
                            {category.category_name}
                        </option>
                    ))}
                </select>
            </div>

            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            )}

            <div className="organizations-table">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading organizations...</p>
                    </div>
                ) : organizations.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-building"></i>
                        <h3>No organizations found</h3>
                        <p>No organizations match your current filters.</p>
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Country</th>
                                        <th>Admin</th>
                                        <th>Status</th>
                                        <th>Subscription</th>
                                        <th>Till Valid Date</th>
                                        <th>Staff Usage</th>
                                        <th>Premises</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {organizations.map(org => (
                                        <tr key={org.id}>
                                            <td>
                                                <div className="org-info">
                                                    <div className="org-logo">
                                                        {org.logo ? (
                                                            <img
                                                                src={getImageUrl(org.logo)}
                                                                alt={org.name}
                                                                className="org-logo-img"
                                                            />
                                                        ) : (
                                                            <div className="org-logo-placeholder">
                                                                {org.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <button
                                                            className="logo-edit-btn"
                                                            onClick={() => openLogoModal(org)}
                                                            title="Update Logo"
                                                        >
                                                            <i className="fas fa-camera"></i>
                                                        </button>
                                                    </div>
                                                    <div className="org-details">
                                                        <h4>{org.name}</h4>
                                                        <small>{org.contact_email}</small>
                                                        <small style={{ display: 'block', color: '#6b7280', marginTop: '2px' }}>
                                                            ID: {org.id}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="category-name">
                                                    {org.country_name || org.country_code || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="admin-info">
                                                    <span className="admin-name">{org.admin_name || org.admin_username || 'N/A'}</span>
                                                    <small>{org.admin_email}</small>
                                                </div>
                                            </td>
                                            <td>
                                                {getStatusBadge(org.status)}
                                            </td>
                                            <td>
                                                {getSubscriptionBadge(org.subscription_status)}
                                                {org.subscription_type && (
                                                    <small className="subscription-type">
                                                        ({org.subscription_type})
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                <small>
                                                    {org.subscription_end_date
                                                        ? orgTimestamp(org, 'subscription_end_date', org.timezone, true)
                                                        : (org.subscription_status === 'active' ? 'No Expiry' : 'N/A')}
                                                </small>
                                            </td>
                                            <td>
                                                <div className="usage-info">
                                                    <span className="usage-numbers">
                                                        {getUsedStaffCount(org)} / {getTotalStaffCount(org)}
                                                    </span>
                                                    <div className="usage-bar">
                                                        <div
                                                            className="usage-fill"
                                                            style={{
                                                                width: `${getUsagePercentage(getUsedStaffCount(org), getTotalStaffCount(org))}%`,
                                                                backgroundColor: getUsagePercentage(getUsedStaffCount(org), getTotalStaffCount(org)) > 80 ? '#f56565' : '#48bb78'
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="premises-actions">
                                                    <button
                                                        className="premises-count-btn"
                                                        onClick={() => openPremisesModal(org)}
                                                        title="View Premises"
                                                    >
                                                        <i className="fas fa-building"></i>
                                                        {org.premises_count || 0}
                                                    </button>
                                                    <button
                                                        className="btn btn-xs btn-outline"
                                                        onClick={() => openCreatePremisesModal(org)}
                                                        title="Add Premises"
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                <small>{orgTimestamp(org, 'created_at', org.timezone, true)}</small>
                                            </td>
                                            <td>
                                                <div className="actions">
                                                    {(org.status == '1') && (
                                                        <button
                                                            className="btn btn-xs btn-warning approval-btn"
                                                            onClick={() => {
                                                                setSelectedOrg(org);
                                                                setShowApprovalModal(true);
                                                            }}
                                                            title="Review & Approve/Reject Application"
                                                        >
                                                            <i className="fas fa-gavel"></i>
                                                            Review
                                                        </button>
                                                    )}
                                                    {/* Organization Status Actions */}
                                                    {org.status === '2' && (
                                                        <button
                                                            className="btn btn-xs btn-warning"
                                                            onClick={() => handleStatusToggle(org, 3)}
                                                            title="Suspend Organization"
                                                        >
                                                            <i className="fas fa-pause"></i>
                                                            Suspend
                                                        </button>
                                                    )}

                                                    {org.status === '0' && (
                                                        <button
                                                            className="btn btn-xs btn-success"
                                                            onClick={() => handleStatusToggle(org, 2)}
                                                            title="Activate Organization"
                                                        >
                                                            <i className="fas fa-play"></i>
                                                            Activate
                                                        </button>
                                                    )}

                                                    {org.status === '3' && (
                                                        <button
                                                            className="btn btn-xs btn-danger"
                                                            onClick={() => handleStatusToggle(org, 0)}
                                                            title="Disable Organization"
                                                        >
                                                            <i className="fas fa-ban"></i>
                                                            Disable
                                                        </button>
                                                    )}

                                                    <button
                                                        className="btn btn-xs btn-outline"
                                                        onClick={() => viewOrganizationDetails(org)}
                                                        title="View Details"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>

                                                    <button
                                                        className="btn btn-xs btn-success"
                                                        onClick={() => openAssignAgentModal(org)}
                                                        title="Assign or reassign sales agent"
                                                    >
                                                        <i className="fas fa-user-tag"></i>
                                                        Agent
                                                    </button>

                                                    <button
                                                        className="btn btn-xs btn-primary"
                                                        onClick={() => openEditModal(org)}
                                                        title="Edit Organization"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>

                                                    {org.status === 2 && (
                                                        <button
                                                            className="btn btn-xs btn-secondary"
                                                            onClick={() => handleStatusToggle(org, 3)}
                                                            title="Suspend Organization"
                                                        >
                                                            <i className="fas fa-pause"></i>
                                                        </button>
                                                    )}

                                                    {org.status === 3 && (
                                                        <button
                                                            className="btn btn-xs btn-success"
                                                            onClick={() => handleStatusToggle(org, 2)}
                                                            title="Activate Organization"
                                                        >
                                                            <i className="fas fa-play"></i>
                                                        </button>
                                                    )}

                                                    <button
                                                        className="btn btn-xs btn-info"
                                                        onClick={() => openBulkPremisesModal(org)}
                                                        title="Bulk Upload Premises"
                                                    >
                                                        <i className="fas fa-upload"></i>
                                                        Bulk
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.total_pages}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </div>

            {/* Bulk Staff Upload Modal */}
            <Modal
                isOpen={showBulkStaffModal}
                onClose={handleCloseBulkStaffModal}
                title={`Bulk Upload Staff - ${selectedPremises?.premises_name || ''} (${selectedOrg?.name || ''})`}
                size="large"
            >
                <form onSubmit={handleBulkStaffUpload}>
                    {/* Upload Method Selection */}
                    <div className="upload-method-selection">
                        <h4>Choose Upload Method:</h4>
                        <div className="radio-group">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="separate"
                                    checked={bulkStaffUploadMethod === 'separate'}
                                    onChange={(e) => setBulkStaffUploadMethod(e.target.value)}
                                />
                                <span>Separate Files (CSV + Staff Images)</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="zip"
                                    checked={bulkStaffUploadMethod === 'zip'}
                                    onChange={(e) => setBulkStaffUploadMethod(e.target.value)}
                                />
                                <span>ZIP Package (CSV + Images in one file)</span>
                            </label>
                        </div>
                    </div>

                    <div className="bulk-upload-info">
                        <div className="info-box">
                            <i className="fas fa-info-circle"></i>
                            <div>
                                <h4>CSV Format Requirements:</h4>
                                <div className="csv-format-warning">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Important:</strong> All fields with commas must be wrapped in double quotes.
                                </div>
                                <ul>
                                    <li><strong>name</strong> (required)</li>
                                    <li><strong>staff_id</strong> (required - unique identifier)</li>
                                    {/* Remove premises_id line */}
                                    <li><strong>designation</strong> (optional - from designations table below)</li>
                                    <li><strong>phone_number</strong> (required - without country code)</li>
                                    <li><strong>country_code</strong> (required - e.g., +234, +1, +44)</li>
                                    <li><strong>email</strong> (required - unique email address)</li>
                                    <li><strong>username</strong> (required - unique username)</li>
                                    <li><strong>gender</strong> (optional - Male, Female, Other)</li>
                                    <li><strong>dob</strong> (optional - YYYY-MM-DD format)</li>
                                    <li><strong>date_of_appointment</strong> (optional - YYYY-MM-DD format)</li>
                                    <li><strong>image_filename</strong> (optional - must match uploaded image files)</li>
                                </ul>

                                <div className="csv-examples">
                                    <h5>Example CSV Row:</h5>
                                    <code>
                                        "John Doe","STF001","Manager","+234","8012345678","john@company.com","johndoe","Male","1990-05-15","2024-01-15","john_photo.jpg"
                                    </code>
                                </div>
                            </div>
                        </div>

                        {/* Designations Reference Table */}
                        {bulkDesignations.length > 0 && (
                            <div className="designations-reference">
                                <h4>Available Designations for {selectedOrg?.category_name}:</h4>
                                <div className="reference-table">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Designation Name</th>
                                                <th>Premises</th>
                                                <th>Category</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkDesignations.map(designation => (
                                                <tr key={designation.id}>
                                                    <td><strong>{designation.designation_name}</strong></td>
                                                    <td>{designation.premises_id === 0 ? 'All Premises' : 'Specific Premises'}</td>
                                                    <td>{designation.category_name}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Organization Premises Reference */}
                        {orgPremises.length > 0 && (
                            <div className="premises-reference">
                                <h4>Organization Premises IDs:</h4>
                                <div className="reference-table">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Premises ID</th>
                                                <th>Premises Name</th>
                                                <th>Address</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orgPremises.map(premises => (
                                                <tr key={premises.id}>
                                                    <td><strong>{premises.id}</strong></td>
                                                    <td>{premises.premises_name}</td>
                                                    <td>{premises.address}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* File Upload Sections */}
                    {bulkStaffUploadMethod === 'zip' ? (
                        <div className="form-group">
                            <label>Upload ZIP Package *</label>
                            <input
                                type="file"
                                accept=".zip"
                                onChange={(e) => setBulkStaffFile(e.target.files[0])}
                                className="form-input"
                                required
                            />
                            <small>Upload a ZIP file containing staff.csv and staff images</small>
                        </div>
                    ) : (
                        <>
                            <div className="form-group">
                                <label>Upload CSV File *</label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => setBulkStaffFile(e.target.files[0])}
                                    className="form-input"
                                    required
                                />
                                <small>Select a CSV file containing staff data</small>
                            </div>

                            <div className="form-group">
                                <label>Upload Staff Images (Optional)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setBulkStaffImages(Array.from(e.target.files))}
                                    className="form-input"
                                />
                                <small>Upload staff images. Filename should match the image_filename column in CSV</small>
                            </div>
                        </>
                    )}

                    {/* Template Download */}
                    <div className="form-group">
                        <label>Download Templates</label>
                        <div className="template-buttons">
                            <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => downloadStaffCSVTemplate(bulkDesignations, selectedPremises)}
                            >
                                <i className="fas fa-download"></i>
                                CSV Template
                            </button>
                        </div>
                        <small>Download template with valid designation and premises data</small>
                    </div>

                    {/* Results Display */}
                    {bulkStaffResult && (
                        <div className="bulk-results">
                            <h4>Upload Results:</h4>
                            <p>✅ Success: {bulkStaffResult.success_count} staff created</p>
                            <p>❌ Errors: {bulkStaffResult.error_count} staff failed</p>

                            {bulkStaffResult.errors && bulkStaffResult.errors.length > 0 && (
                                <div className="error-details">
                                    <h5>Error Details:</h5>
                                    <ul>
                                        {bulkStaffResult.errors.slice(0, 5).map((error, index) => (
                                            <li key={index}>
                                                Row {error.row}: {error.message}
                                            </li>
                                        ))}
                                    </ul>
                                    {bulkStaffResult.errors.length > 5 && (
                                        <p>... and {bulkStaffResult.errors.length - 5} more errors</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseBulkStaffModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={bulkStaffLoading || !bulkStaffFile}
                        >
                            {bulkStaffLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload"></i>
                                    Upload Staff
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Bulk Premises Upload Modal */}
            <Modal
                isOpen={showBulkPremisesModal}
                onClose={handleCloseBulkPremisesModal}
                title={`Bulk Upload Premises - ${selectedOrg?.name || ''}`}
                size="large"
            >
                <form onSubmit={handleBulkPremisesUpload}>
                    {/* Upload Method Selection */}
                    <div className="upload-method-selection">
                        <h4>Choose Upload Method:</h4>
                        <div className="radio-group">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="separate"
                                    checked={uploadMethod === 'separate'}
                                    onChange={(e) => setUploadMethod(e.target.value)}
                                />
                                <span>Separate Files (CSV + Logo Files)</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="zip"
                                    checked={uploadMethod === 'zip'}
                                    onChange={(e) => setUploadMethod(e.target.value)}
                                />
                                <span>ZIP Package (CSV + Logos in one file)</span>
                            </label>
                        </div>
                    </div>

                    <div className="bulk-upload-info">
                        <div className="info-box">
                            <i className="fas fa-info-circle"></i>
                            <div>
                                {uploadMethod === 'zip' ? (
                                    <>
                                        <h4>ZIP Package Requirements:</h4>
                                        <ul>
                                            <li><strong>premises.csv</strong> - CSV file with premises data</li>
                                            <li><strong>Logo files</strong> - Image files (jpg, png, gif) in root or logos/ folder</li>
                                            <li><strong>Structure example:</strong>
                                                <pre className="zip-structure">
                                                    {`premises-upload.zip
                                                        ├── premises.csv
                                                        ├── office_logo.jpg
                                                        ├── branch_logo.png
                                                        └── logos/
                                                            ├── warehouse_logo.gif
                                                            └── factory_logo.jpg`}
                                                </pre>
                                            </li>
                                        </ul>
                                    </>
                                ) : (
                                    <>
                                        <h4>CSV Format Requirements:</h4>
                                        <div className="csv-format-warning">
                                            <i className="fas fa-exclamation-triangle"></i>
                                            <strong>Important:</strong> For fields containing commas (addresses, GPS coordinates, descriptions),
                                            wrap the entire value in double quotes.
                                        </div>
                                        <p>Your CSV file should contain the following columns:</p>
                                        <ul>
                                            <li><strong>premises_name</strong> (required)</li>
                                            <li><strong>address</strong> (required) - Wrap in quotes if contains commas: <code>"123 Main St, Suite 100"</code></li>
                                            <li><strong>premises_type_id</strong> (required - use ID from table below)</li>
                                            <li><strong>city_id</strong> (optional)</li>
                                            <li><strong>poc_name</strong> (optional)</li>
                                            <li><strong>phone_number</strong> (optional)</li>
                                            <li><strong>contact_email</strong> (optional)</li>
                                            <li><strong>description</strong> (optional) - Wrap in quotes if contains commas</li>
                                            <li><strong>weekly_off_days</strong> (optional - comma separated: Monday,Sunday)</li>
                                            <li><strong>gps_coordinates1</strong> (optional) - Format: <code>"latitude,longitude"</code></li>
                                            <li><strong>gps_coordinates2</strong> (optional) - Format: <code>"latitude,longitude"</code></li>
                                            <li><strong>gps_coordinates3</strong> (optional) - Format: <code>"latitude,longitude"</code></li>
                                            <li><strong>gps_coordinates4</strong> (optional) - Format: <code>"latitude,longitude"</code></li>
                                            <li><strong>logo_filename</strong> (optional - must match uploaded logo files)</li>
                                        </ul>

                                        <div className="csv-examples">
                                            <h5>Examples of Proper CSV Formatting:</h5>
                                            <ul>
                                                <li>Address: <code>"123 Business Street, Block A, Victoria Island"</code></li>
                                                <li>GPS: <code>"6.5244,3.3792"</code></li>
                                                <li>Description: <code>"Main office, ground floor, reception area"</code></li>
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Premises Types Reference Table */}
                        {bulkPremisesTypes.length > 0 && (
                            <div className="premises-types-reference">
                                <h4>Premises Types Reference for {selectedOrg?.name || 'Organization'}:</h4>
                                <div className="reference-table">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Type ID</th>
                                                <th>Type Name</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkPremisesTypes.map(type => (
                                                <tr key={type.id}>
                                                    <td><strong>{type.id}</strong></td>
                                                    <td>{type.premisese_type_name}</td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(type.id);
                                                                showToast(`Premises Type ID ${type.id} copied!`, 'success');
                                                            }}
                                                            title="Copy Premises Type ID"
                                                        >
                                                            <i className="fas fa-copy"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* City IDs Reference Table */}
                        {bulkPremisesTypes.length > 0 && (
                            <div className="cities-reference">
                                <h4>City IDs Reference - {selectedOrg?.name || 'Loading...'}:</h4>
                                <div className="city-lookup-controls">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Search Cities:</label>
                                            <input
                                                type="text"
                                                value={citySearch}
                                                onChange={(e) => handleCitySearchForBulk(e.target.value)}
                                                className="form-input"
                                                placeholder="Type to search cities..."
                                                disabled={citiesLoading}
                                            />
                                            <small>Enter at least 2 characters to search cities in {selectedOrg?.country_name || 'organization country'}</small>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline btn-xs"
                                        onClick={() => setShowCitiesTable(!showCitiesTable)}
                                    >
                                        <i className={`fas fa-chevron-${showCitiesTable ? 'up' : 'down'}`}></i>
                                        {showCitiesTable ? 'Hide' : 'Show'} Cities Table
                                    </button>
                                </div>

                                {citiesLoading && (
                                    <div className="loading-cities">
                                        <div className="spinner"></div>
                                        <span>Loading cities...</span>
                                    </div>
                                )}

                                {showCitiesTable && bulkCities.length > 0 && (
                                    <div className="cities-table-container">
                                        <div className="cities-count">
                                            <small>Showing {bulkCities.length} cities {citySearch && `matching "${citySearch}"`}</small>
                                        </div>
                                        <div className="reference-table cities-table">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>City ID</th>
                                                        <th>City Name</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bulkCities.slice(0, 100).map(city => (
                                                        <tr key={city.id}>
                                                            <td><strong>{city.id}</strong></td>
                                                            <td>{city.name}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(city.id);
                                                                        showToast(`City ID ${city.id} copied!`, 'success');
                                                                    }}
                                                                    title="Copy City ID"
                                                                >
                                                                    <i className="fas fa-copy"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {bulkCities.length > 100 && (
                                                <div className="cities-limit-notice">
                                                    <small className="text-muted">
                                                        Showing first 100 cities. Use search to find specific cities.
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {showCitiesTable && bulkCities.length === 0 && !citiesLoading && selectedCountryForBulk && (
                                    <div className="no-cities-found">
                                        <small className="text-muted">
                                            {citySearch ? `No cities found matching "${citySearch}"` : 'No cities found for selected country'}
                                        </small>
                                    </div>
                                )}

                                <div className="city-info-note">
                                    <i className="fas fa-info-circle"></i>
                                    <small>
                                        City ID is optional. Leave empty if you don't need to specify a city for the premises.
                                        You can search and copy city IDs from the table above.
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* File Upload Sections */}
                    {uploadMethod === 'zip' ? (
                        <div className="form-group">
                            <label>Upload ZIP Package *</label>
                            <input
                                type="file"
                                accept=".zip"
                                onChange={(e) => setBulkPremisesFile(e.target.files[0])}
                                className="form-input"
                                required
                                disabled={bulkPremisesTypes.length === 0}
                            />
                            <small>Upload a ZIP file containing premises.csv and logo images</small>
                        </div>
                    ) : (
                        <>
                            <div className="form-group">
                                <label>Upload CSV File *</label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => setBulkPremisesFile(e.target.files[0])}
                                    className="form-input"
                                    required
                                    disabled={bulkPremisesTypes.length === 0}
                                />
                                <small>Select a CSV file containing premises data</small>
                            </div>

                            <div className="form-group">
                                <label>Upload Logo Files (Optional)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setLogoFiles(Array.from(e.target.files))}
                                    className="form-input"
                                    disabled={bulkPremisesTypes.length === 0}
                                />
                                <small>Upload logo images. Filename should match the logo_filename column in CSV</small>
                            </div>
                        </>
                    )}

                    {/* Template Downloads */}
                    <div className="form-group">
                        <label>Download Templates</label>
                        <div className="template-buttons">
                            <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => downloadCSVTemplate(bulkPremisesTypes)}
                                disabled={bulkPremisesTypes.length === 0}
                            >
                                <i className="fas fa-download"></i>
                                CSV Template
                            </button>
                            {uploadMethod === 'zip' && (
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={() => downloadZipTemplate(bulkPremisesTypes)}
                                    disabled={bulkPremisesTypes.length === 0}
                                >
                                    <i className="fas fa-file-archive"></i>
                                    ZIP Template
                                </button>
                            )}
                        </div>
                        <small>Download template files with valid premises type IDs</small>
                    </div>

                    {/* Rest of the modal remains the same */}
                    {bulkPremisesResult && (
                        <div className="bulk-results">
                            <h4>Upload Results:</h4>
                            <p>✅ Success: {bulkPremisesResult.success_count} premises created</p>
                            <p>❌ Errors: {bulkPremisesResult.error_count} premises failed</p>

                            {bulkPremisesResult.errors && bulkPremisesResult.errors.length > 0 && (
                                <div className="error-details">
                                    <h5>Error Details:</h5>
                                    <ul>
                                        {bulkPremisesResult.errors.slice(0, 5).map((error, index) => (
                                            <li key={index}>
                                                Row {error.row}: {error.message}
                                            </li>
                                        ))}
                                    </ul>
                                    {bulkPremisesResult.errors.length > 5 && (
                                        <p>... and {bulkPremisesResult.errors.length - 5} more errors</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseBulkPremisesModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={bulkPremisesLoading || !bulkPremisesFile || bulkPremisesTypes.length === 0}
                        >
                            {bulkPremisesLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload"></i>
                                    Upload Premises
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Create Organization Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={handleCloseCreateModal}
                title="Create New Organization"
                size="medium"
            >
                <form onSubmit={handleCreateSubmit}>
                    <div className="form-group">
                        <label>Organization Name *</label>
                        <input
                            type="text"
                            value={createForm.name}
                            onChange={(e) => handleCreateFormChange('name', e.target.value)}
                            className={createErrors.name ? 'form-input error' : 'form-input'}
                            placeholder="Enter organization name"
                            required
                        />
                        {createErrors.name && <span className="form-error">{createErrors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>Country *</label>
                        <SearchableSelect
                            value={createForm.country_id}
                            onChange={(value) => handleCreateFormChange('country_id', value)}
                            options={countries}
                            placeholder="Select Country"
                            displayKey="name"
                            valueKey="id"
                            error={!!createErrors.country_id}
                            searchPlaceholder="Search countries..."
                        />
                        {createErrors.country_id && <span className="form-error">{createErrors.country_id}</span>}
                    </div>

                    <div className="form-group">
                        <label>Category *</label>
                        <SearchableSelect
                            value={createForm.org_category_id}
                            onChange={(value) => handleCreateFormChange('org_category_id', value)}
                            options={categories}
                            placeholder="Select Category"
                            displayKey="category_name"
                            valueKey="id"
                            error={!!createErrors.org_category_id}
                            searchPlaceholder="Search categories..."
                        />
                        {createErrors.org_category_id && <span className="form-error">{createErrors.org_category_id}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Admin Username *</label>
                            <input
                                type="text"
                                value={createForm.admin_username}
                                onChange={(e) => handleCreateFormChange('admin_username', e.target.value)}
                                className={createErrors.admin_username ? 'form-input error' : 'form-input'}
                                placeholder="Admin username"
                                required
                            />
                            {createErrors.admin_username && <span className="form-error">{createErrors.admin_username}</span>}
                        </div>
                        <div className="form-group">
                            <label>Admin Phone</label>
                            <input
                                type="tel"
                                value={createForm.admin_phone}
                                onChange={(e) => handleCreateFormChange('admin_phone', e.target.value)}
                                className="form-input"
                                placeholder="Phone number"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Admin Email *</label>
                        <input
                            type="email"
                            value={createForm.admin_email}
                            onChange={(e) => handleCreateFormChange('admin_email', e.target.value)}
                            className={createErrors.admin_email ? 'form-input error' : 'form-input'}
                            placeholder="admin@organization.com"
                            required
                        />
                        {createErrors.admin_email && <span className="form-error">{createErrors.admin_email}</span>}
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={createForm.description}
                            onChange={(e) => handleCreateFormChange('description', e.target.value)}
                            className="form-textarea"
                            placeholder="Organization description (optional)"
                            rows="3"
                        />
                    </div>

                    {createErrors.general && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {createErrors.general}
                        </div>
                    )}

                    <div className="info-box">
                        <i className="fas fa-info-circle"></i>
                        <p>The admin user will receive a welcome email with temporary login credentials.</p>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseCreateModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={createLoading}
                        >
                            {createLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-plus"></i>
                                    Create Organization
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Organization Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={handleCloseEditModal}
                title="Edit Organization"
                size="medium"
            >
                <form onSubmit={handleEditSubmit}>
                    <div className="form-group">
                        <label>Organization Name *</label>
                        <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => handleEditFormChange('name', e.target.value)}
                            className={editErrors.name ? 'form-input error' : 'form-input'}
                            placeholder="Enter organization name"
                            required
                        />
                        {editErrors.name && <span className="form-error">{editErrors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>Category *</label>
                        <SearchableSelect
                            value={editForm.org_category_id}
                            onChange={(value) => handleEditFormChange('org_category_id', value)}
                            options={categories}
                            placeholder="Select Category"
                            displayKey="category_name"
                            valueKey="id"
                            error={!!editErrors.org_category_id}
                            searchPlaceholder="Search categories..."
                        />
                        {editErrors.org_category_id && <span className="form-error">{editErrors.org_category_id}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Contact Email *</label>
                            <input
                                type="email"
                                value={editForm.contact_email}
                                onChange={(e) => handleEditFormChange('contact_email', e.target.value)}
                                className={editErrors.contact_email ? 'form-input error' : 'form-input'}
                                placeholder="contact@organization.com"
                                required
                            />
                            {editErrors.contact_email && <span className="form-error">{editErrors.contact_email}</span>}
                        </div>
                        <div className="form-group">
                            <label>Contact Phone</label>
                            <input
                                type="tel"
                                value={editForm.contact_phone}
                                onChange={(e) => handleEditFormChange('contact_phone', e.target.value)}
                                className="form-input"
                                placeholder="Phone number"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={editForm.description}
                            onChange={(e) => handleEditFormChange('description', e.target.value)}
                            className="form-textarea"
                            placeholder="Organization description"
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label>Tax Value (%) *</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={editForm.tax_value}
                            onChange={(e) => handleEditFormChange('tax_value', e.target.value)}
                            className="form-input"
                            placeholder="e.g., 15.00"
                            required
                        />
                        <p style={{ marginTop: '8px', fontSize: '13px', color: '#718096' }}>
                            Tax percentage to be added to subscription payments (0-100%)
                        </p>
                    </div>

                    <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', margin: 0 }}>
                            <input
                                type="checkbox"
                                checked={editForm.integration_enabled == 1}
                                onChange={(e) => handleEditFormChange('integration_enabled', e.target.checked ? 1 : 0)}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '15px' }}>
                                <i className="fas fa-plug" style={{ marginRight: '8px', color: '#667eea' }}></i>
                                Enable API Integration
                            </span>
                        </label>
                        <p style={{ marginTop: '8px', marginLeft: '28px', fontSize: '13px', color: '#718096', marginBottom: 0 }}>
                            Enable this option if the organization requires API integration capabilities
                        </p>
                    </div>

                    <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', margin: 0 }}>
                            <input
                                type="checkbox"
                                checked={editForm.lock_offsite_radius_settings == 1}
                                onChange={(e) => handleEditFormChange('lock_offsite_radius_settings', e.target.checked ? 1 : 0)}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '15px' }}>
                                <i className="fas fa-lock" style={{ marginRight: '8px', color: '#667eea' }}></i>
                                Lock Off-site Radius Settings
                            </span>
                        </label>
                        <p style={{ marginTop: '8px', marginLeft: '28px', fontSize: '13px', color: '#718096', marginBottom: 0 }}>
                            Prevent this organization's admins from changing off-site attendance radius options.
                        </p>
                    </div>

                    {editErrors.general && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {editErrors.general}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseEditModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={editLoading}
                        >
                            {editLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i>
                                    Update Organization
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Create Premises Modal */}
            <Modal
                isOpen={showCreatePremisesModal}
                onClose={handleCloseCreatePremisesModal}
                title={`Add Premises - ${selectedOrg?.name || ''}`}
                size="large"
            >
                <form onSubmit={handleCreatePremises}>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Premises Type *</label>
                            <SearchableSelect
                                value={premisesForm.premises_type_id}
                                onChange={(value) => handlePremisesFormChange('premises_type_id', value)}
                                options={premisesTypes}
                                placeholder="Select Premises Type"
                                displayKey="premisese_type_name"
                                valueKey="id"
                                error={!!premisesErrors.premises_type_id}
                                disabled={premisesTypes.length === 0}
                                searchPlaceholder="Search premises types..."
                            />
                            {premisesErrors.premises_type_id && <span className="form-error">{premisesErrors.premises_type_id}</span>}
                            {premisesTypes.length === 0 && selectedOrg?.category_name && (
                                <small className="form-help text-muted">
                                    No premises types configured for "{selectedOrg.category_name}" category
                                </small>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Premises Name *</label>
                            <input
                                type="text"
                                value={premisesForm.premises_name}
                                onChange={(e) => handlePremisesFormChange('premises_name', e.target.value)}
                                className={premisesErrors.premises_name ? 'form-input error' : 'form-input'}
                                placeholder="Enter premises name"
                                required
                            />
                            {premisesErrors.premises_name && <span className="form-error">{premisesErrors.premises_name}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Address *</label>
                        <textarea
                            value={premisesForm.address}
                            onChange={(e) => handlePremisesFormChange('address', e.target.value)}
                            className={premisesErrors.address ? 'form-textarea error' : 'form-textarea'}
                            placeholder="Enter full address"
                            rows="3"
                            required
                        />
                        {premisesErrors.address && <span className="form-error">{premisesErrors.address}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>City - {selectedOrg?.name || 'Loading...'}</label>
                            <SearchableSelect
                                value={premisesForm.city_id}
                                onChange={(value) => handlePremisesFormChange('city_id', value)}
                                options={cities}
                                placeholder={`Select City in ${selectedOrg?.country_name || 'organization country'}`}
                                displayKey="name"
                                valueKey="id"
                                loading={citiesLoading}
                                onSearch={async (searchTerm) => {
                                    if (selectedOrg?.country_code && searchTerm.length >= 2) {
                                        try {
                                            setCitiesLoading(true);
                                            const data = await countryService.getCitiesByCountryCode(selectedOrg.country_code, searchTerm);
                                            setCities(data.cities || []);
                                        } catch (err) {
                                            console.error('Error searching cities:', err);
                                            setCities([]);
                                        } finally {
                                            setCitiesLoading(false);
                                        }
                                    }
                                }}
                                searchPlaceholder="Type to search cities..."
                                minSearchLength={2}
                                error={!!premisesErrors.city_id}
                            />
                            {citiesLoading && <small>Loading cities...</small>}
                            <small className="form-help">Cities are loaded from {selectedOrg?.country_name || 'organization country'}</small>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Point of Contact</label>
                            <input
                                type="text"
                                value={premisesForm.poc_name}
                                onChange={(e) => handlePremisesFormChange('poc_name', e.target.value)}
                                className="form-input"
                                placeholder="Contact person name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                value={premisesForm.phone_number}
                                onChange={(e) => handlePremisesFormChange('phone_number', e.target.value)}
                                className="form-input"
                                placeholder="Contact phone number"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Contact Email</label>
                        <input
                            type="email"
                            value={premisesForm.contact_email}
                            onChange={(e) => handlePremisesFormChange('contact_email', e.target.value)}
                            className="form-input"
                            placeholder="premises@organization.com"
                        />
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={premisesForm.description}
                            onChange={(e) => handlePremisesFormChange('description', e.target.value)}
                            className="form-textarea"
                            placeholder="Premises description (optional)"
                            rows="2"
                        />
                    </div>

                    <div className="form-group">
                        <label>Weekly Off Days</label>
                        <div className="checkbox-group">
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={premisesForm.weekly_off_days.includes(day)}
                                        onChange={(e) => {
                                            const days = [...premisesForm.weekly_off_days];
                                            if (e.target.checked) {
                                                days.push(day);
                                            } else {
                                                const index = days.indexOf(day);
                                                if (index > -1) days.splice(index, 1);
                                            }
                                            handlePremisesFormChange('weekly_off_days', days);
                                        }}
                                    />
                                    {day}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Geofence Settings</h4>
                        <p className="form-help">Define the boundary points for this premises. Enter the latitude and longitude for each corner point.</p>

                        <div className="geofence-grid">
                            {[1, 2, 3, 4].map(pointNum => (
                                <div key={pointNum} className="geofence-point">
                                    <h5>Point {pointNum}</h5>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Latitude</label>
                                            <input
                                                type="number"
                                                step="any"
                                                value={premisesForm.geofence[`point${pointNum}`].latitude}
                                                onChange={(e) => handleGeofencePointChange(pointNum, 'latitude', e.target.value)}
                                                className="form-input"
                                                placeholder="6.5244"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Longitude</label>
                                            <input
                                                type="number"
                                                step="any"
                                                value={premisesForm.geofence[`point${pointNum}`].longitude}
                                                onChange={(e) => handleGeofencePointChange(pointNum, 'longitude', e.target.value)}
                                                className="form-input"
                                                placeholder="3.3792"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="info-box">
                            <i className="fas fa-info-circle"></i>
                            <p>Points should be entered in clockwise or counter-clockwise order to form a proper boundary polygon.</p>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Premises Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleImageUpload(file, 'premises');
                                }
                            }}
                            className="form-input"
                        />
                        <small>Upload a logo for this premises (optional)</small>
                    </div>

                    {premisesErrors.general && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {premisesErrors.general}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseCreatePremisesModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={premisesCreateLoading}
                        >
                            {premisesCreateLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-plus"></i>
                                    Create Premises
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Organization Logo Update Modal */}
            <Modal
                isOpen={showLogoModal}
                onClose={handleCloseLogoModal}
                title="Update Organization Logo"
                size="small"
            >
                <form onSubmit={handleLogoUpdate}>
                    <div className="form-group">
                        <label>Upload New Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleImageUpload(file, 'organization');
                                }
                            }}
                            className="form-input"
                            required
                        />
                        <small>Supported formats: JPG, PNG, GIF (Max 2MB)</small>
                    </div>

                    {logoForm.logo_preview && (
                        <div className="logo-preview">
                            <label>Preview:</label>
                            <img
                                src={logoForm.logo_preview}
                                alt="Logo Preview"
                                className="logo-preview-img"
                            />
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseLogoModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={logoUpdateLoading || !logoForm.logo_base64}
                        >
                            {logoUpdateLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload"></i>
                                    Update Logo
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Premises Modal - Add this after the Create Premises Modal */}
            <Modal
                isOpen={showEditPremisesModal}
                onClose={handleCloseEditPremisesModal}
                title={`Edit Premises - ${selectedPremises?.premises_name || ''}`}
                size="large"
            >
                <form onSubmit={handleEditPremises}>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Premises Type *</label>
                            <SearchableSelect
                                value={premisesForm.premises_type_id}
                                onChange={(value) => handlePremisesFormChange('premises_type_id', value)}
                                options={premisesTypes}
                                placeholder="Select Premises Type"
                                displayKey="premisese_type_name"
                                valueKey="id"
                                error={!!premisesErrors.premises_type_id}
                                searchPlaceholder="Search premises types..."
                            />
                            {premisesErrors.premises_type_id && <span className="form-error">{premisesErrors.premises_type_id}</span>}
                        </div>

                        <div className="form-group">
                            <label>Premises Name *</label>
                            <input
                                type="text"
                                value={premisesForm.premises_name}
                                onChange={(e) => handlePremisesFormChange('premises_name', e.target.value)}
                                className={premisesErrors.premises_name ? 'form-input error' : 'form-input'}
                                placeholder="Enter premises name"
                                required
                            />
                            {premisesErrors.premises_name && <span className="form-error">{premisesErrors.premises_name}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Address *</label>
                        <textarea
                            value={premisesForm.address}
                            onChange={(e) => handlePremisesFormChange('address', e.target.value)}
                            className={premisesErrors.address ? 'form-textarea error' : 'form-textarea'}
                            placeholder="Enter full address"
                            rows="3"
                            required
                        />
                        {premisesErrors.address && <span className="form-error">{premisesErrors.address}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>City (Optional) - {selectedOrg?.country_name || 'Loading...'}</label>
                            <SearchableSelect
                                value={premisesForm.city_id}
                                onChange={(value) => handlePremisesFormChange('city_id', value)}
                                options={cities}
                                placeholder={`Select City in ${selectedOrg?.country_name || 'organization country'}`}
                                displayKey="name"
                                valueKey="id"
                                loading={citiesLoading}
                                onSearch={async (searchTerm) => {
                                    if (selectedOrg?.country_code && searchTerm.length >= 2) {
                                        try {
                                            setCitiesLoading(true);
                                            const data = await countryService.getCitiesByCountryCode(selectedOrg.country_code, searchTerm);
                                            setCities(data.cities || []);
                                        } catch (err) {
                                            console.error('Error searching cities:', err);
                                            setCities([]);
                                        } finally {
                                            setCitiesLoading(false);
                                        }
                                    }
                                }}
                                searchPlaceholder="Type to search cities..."
                                minSearchLength={2}
                                error={!!premisesErrors.city_id}
                            />
                            {citiesLoading && <small>Loading cities...</small>}
                            <small className="form-help">Cities are loaded from {selectedOrg?.country_name || 'organization country'}</small>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Point of Contact</label>
                            <input
                                type="text"
                                value={premisesForm.poc_name}
                                onChange={(e) => handlePremisesFormChange('poc_name', e.target.value)}
                                className="form-input"
                                placeholder="Contact person name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                value={premisesForm.phone_number}
                                onChange={(e) => handlePremisesFormChange('phone_number', e.target.value)}
                                className="form-input"
                                placeholder="Contact phone number"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Contact Email</label>
                        <input
                            type="email"
                            value={premisesForm.contact_email}
                            onChange={(e) => handlePremisesFormChange('contact_email', e.target.value)}
                            className="form-input"
                            placeholder="premises@organization.com"
                        />
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={premisesForm.description}
                            onChange={(e) => handlePremisesFormChange('description', e.target.value)}
                            className="form-textarea"
                            placeholder="Premises description (optional)"
                            rows="2"
                        />
                    </div>

                    <div className="form-group">
                        <label>Weekly Off Days</label>
                        <div className="checkbox-group">
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={premisesForm.weekly_off_days.includes(day)}
                                        onChange={(e) => {
                                            const days = [...premisesForm.weekly_off_days];
                                            if (e.target.checked) {
                                                days.push(day);
                                            } else {
                                                const index = days.indexOf(day);
                                                if (index > -1) days.splice(index, 1);
                                            }
                                            handlePremisesFormChange('weekly_off_days', days);
                                        }}
                                    />
                                    {day}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Use the new 4-point geofence section here if you've implemented it */}

                    <div className="form-group">
                        <label>Update Premises Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleImageUpload(file, 'premises');
                                }
                            }}
                            className="form-input"
                        />
                        <small>Upload a new logo for this premises (optional)</small>
                    </div>

                    {premisesErrors.general && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {premisesErrors.general}
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseEditPremisesModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={premisesEditLoading}
                        >
                            {premisesEditLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i>
                                    Update Premises
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Organization Premises Management Modal */}
            <Modal
                isOpen={showPremisesModal}
                onClose={handleClosePremisesModal}
                title={`Premises Management - ${selectedOrg?.name || ''}`}
                size="large"
            >
                <div className="premises-management">
                    <div className="premises-header">
                        <h4>Organization Premises</h4>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                                setShowPremisesModal(false);
                                openCreatePremisesModal(selectedOrg);
                            }}
                        >
                            <i className="fas fa-plus"></i>
                            Add Premises
                        </button>

                    </div>

                    {premisesLoading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading premises...</p>
                        </div>
                    ) : orgPremises.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-building"></i>
                            <h4>No premises found</h4>
                            <p>This organization has no premises configured yet.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowPremisesModal(false);
                                    openCreatePremisesModal(selectedOrg);
                                }}
                            >
                                <i className="fas fa-plus"></i>
                                Add First Premises
                            </button>
                        </div>
                    ) : (
                        <div className="premises-list">
                            {orgPremises.map(premises => (
                                <div key={premises.id} className="premises-card">
                                    <div className="premises-header-info">
                                        <div className="premises-logo">
                                            {premises.logo ? (
                                                <img
                                                    src={getImageUrl(premises.logo)}
                                                    alt={premises.premises_name}
                                                    className="premises-logo-img"
                                                />
                                            ) : (
                                                <div className="premises-logo-placeholder">
                                                    <i className="fas fa-building"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="premises-info">
                                            <h5>{premises.premises_name}</h5>
                                            <p className="premises-type">{premises.premisese_type_name}</p>
                                            <p className="premises-address">{premises.address}</p>
                                            {premises.city_name && (
                                                <p className="premises-city">{premises.city_name}</p>
                                            )}
                                            <div className="premises-status-badge">
                                                {Number(premises.status) === 1 && <span className="badge badge-success">🟢 Active</span>}
                                                {Number(premises.status) === 2 && <span className="badge badge-info">🔵 Demo</span>}
                                                {Number(premises.status) === 0 && <span className="badge badge-danger">🔴 Inactive</span>}
                                                {Number(premises.status) === 3 && <span className="badge badge-warning">⭐ Special</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="premises-meta">
                                        <div className="meta-item">
                                            <i className="fas fa-users"></i>
                                            <span>Staff: {premises.staff_count || 0}</span>
                                        </div>
                                    </div>

                                    {/* Premises Status Dropdown */}
                                    <div className="premises-actions">
                                        {/* Status Change Dropdown */}
                                        <select
                                            value={premises.status ?? 1}
                                            onChange={(e) => handlePremisesStatusChange(premises.id, parseInt(e.target.value))}
                                            className="btn btn-xs btn-outline premises-status-select"
                                            title="Change Status"
                                        >
                                            <option value={1}>Active</option>
                                            <option value={2}>Demo</option>
                                            <option value={0}>Inactive</option>
                                            <option value={3}>Special</option>
                                        </select>

                                        <button
                                            className="btn btn-xs btn-outline"
                                            onClick={() => openEditPremisesModal(premises)}
                                            title="Edit Premises"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>

                                        <button
                                            className="btn btn-xs btn-success"
                                            onClick={() => openBulkStaffModal(selectedOrg, premises)}
                                            title="Bulk Upload Staff for this Premises"
                                        >
                                            <i className="fas fa-file"></i>
                                            Bulk Staff Upload
                                        </button>

                                        <button
                                            className="btn btn-xs btn-danger"
                                            onClick={() => handleDeletePremises(premises)}
                                            title="Delete Premises"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={handleCloseDeleteModal}
                title="Delete Organization"
                size="small"
            >
                {selectedOrg && (
                    <div className="delete-confirmation">
                        <div className="warning-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h3>Are you sure?</h3>
                        <p>
                            You are about to permanently delete <strong>{selectedOrg.name}</strong>.
                            This action cannot be undone and will remove all associated data including:
                        </p>
                        <ul className="deletion-list">
                            <li>All premises and their configurations</li>
                            <li>All staff members and user accounts</li>
                            <li>All attendance records</li>
                            <li>All leave requests and approvals</li>
                            <li>All payment and subscription data</li>
                        </ul>

                        <div className="confirmation-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCloseDeleteModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <div className="spinner"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-trash"></i>
                                        Delete Organization
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Approval Modal */}
            <Modal
                isOpen={showApprovalModal}
                onClose={handleCloseApprovalModal}
                title="Organization Approval"
                size="medium"
            >
                {selectedOrg && (
                    <form onSubmit={handleApproval}>
                        <div className="org-details">
                            <h3>{selectedOrg.name}</h3>
                            <p><strong>Country:</strong> {selectedOrg.country_name || selectedOrg.country_code || 'N/A'}</p>
                            <p><strong>Admin:</strong> {selectedOrg.admin_name || selectedOrg.admin_username} ({selectedOrg.admin_email})</p>
                            <p><strong>Contact Email:</strong> {selectedOrg.contact_email}</p>
                            <p><strong>Created:</strong> {orgTimestamp(selectedOrg, 'created_at', selectedOrg.timezone)}</p>
                        </div>

                        <div className="form-group">
                            <label>Decision *</label>
                            <select
                                value={approvalForm.action}
                                onChange={(e) => setApprovalForm({ ...approvalForm, action: e.target.value })}
                                className="form-input"
                                required
                            >
                                <option value="">Select Action</option>
                                <option value="approve">Approve Organization</option>
                                <option value="reject">Reject Organization</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Comments {approvalForm.action === 'reject' ? '*' : '(Optional)'}</label>
                            <textarea
                                value={approvalForm.comments}
                                onChange={(e) => setApprovalForm({ ...approvalForm, comments: e.target.value })}
                                className="form-textarea"
                                placeholder="Provide feedback or reason for decision..."
                                rows="4"
                                required={approvalForm.action === 'reject'}
                            />
                        </div>

                        {/* Sales Agent Assignment - Only show when approving */}
                        {approvalForm.action === 'approve' && (
                            <div className="sales-agent-section">
                                <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem', color: '#2d3748', fontSize: '1.1rem' }}>
                                    <i className="fas fa-user-tie"></i> Sales Agent Assignment
                                </h4>

                                <div className="form-row">
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Sales Agent *</label>
                                        <select
                                            value={approvalForm.sales_agent_id}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, sales_agent_id: e.target.value })}
                                            className="form-input"
                                            required
                                        >
                                            {salesAgents.map(agent => (
                                                <option key={agent.id} value={agent.id}>
                                                    {agent.name}
                                                    {agent.id === 1 && ' (Default)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Commission Type *</label>
                                        <select
                                            value={approvalForm.sales_agent_commission_type}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, sales_agent_commission_type: e.target.value })}
                                            className="form-input"
                                            required
                                        >
                                            <option value="percentage">Percentage (%)</option>
                                            <option value="fixed">Fixed Amount</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Commission Value *</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max={approvalForm.sales_agent_commission_type === 'percentage' ? '100' : undefined}
                                            value={approvalForm.sales_agent_commission_value}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, sales_agent_commission_value: e.target.value })}
                                            className="form-input"
                                            placeholder={approvalForm.sales_agent_commission_type === 'percentage' ? 'e.g., 10.00' : 'e.g., 500.00'}
                                            required
                                        />
                                    </div>

                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Start Date *</label>
                                        <input
                                            type="date"
                                            value={approvalForm.sales_agent_start_date}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, sales_agent_start_date: e.target.value })}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>End Date (Optional)</label>
                                    <input
                                        type="date"
                                        value={approvalForm.sales_agent_end_date}
                                        onChange={(e) => setApprovalForm({ ...approvalForm, sales_agent_end_date: e.target.value })}
                                        className="form-input"
                                        min={approvalForm.sales_agent_start_date}
                                    />
                                </div>

                                <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={approvalForm.integration_enabled == 1}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, integration_enabled: e.target.checked ? 1 : 0 })}
                                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontWeight: '600', fontSize: '15px' }}>
                                            <i className="fas fa-plug" style={{ marginRight: '8px', color: '#667eea' }}></i>
                                            Enable API Integration
                                        </span>
                                    </label>
                                    <p style={{ marginTop: '8px', marginLeft: '28px', fontSize: '13px', color: '#718096' }}>
                                        Enable this option if the organization requires API integration capabilities
                                    </p>
                                </div>

                                <div className="form-group" style={{ marginTop: '1rem' }}>
                                    <label>Tax Value (%) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={approvalForm.tax_value}
                                        onChange={(e) => setApprovalForm({ ...approvalForm, tax_value: e.target.value })}
                                        className="form-input"
                                        placeholder="e.g., 15.00"
                                        required
                                    />
                                    <p style={{ marginTop: '8px', fontSize: '13px', color: '#718096' }}>
                                        Tax percentage to be added to subscription payments (0-100%)
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="approval-note">
                            <i className="fas fa-info-circle"></i>
                            <p>An email notification will be sent to the organization admin with your decision and comments.</p>
                        </div>

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCloseApprovalModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`btn ${approvalForm.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                                disabled={!approvalForm.action}
                            >
                                <i className={`fas ${approvalForm.action === 'approve' ? 'fa-check' : 'fa-times'}`}></i>
                                {approvalForm.action === 'approve' ? 'Approve Organization' : 'Reject Organization'}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal
                isOpen={showAssignAgentModal}
                onClose={handleCloseAssignAgentModal}
                title="Assign Sales Agent"
                size="medium"
            >
                {selectedOrg && (
                    <form onSubmit={handleAssignAgent}>
                        <div className="org-details">
                            <h3>{selectedOrg.name}</h3>
                            <p><strong>Organization ID:</strong> {selectedOrg.id}</p>
                        </div>

                        <div className="form-group">
                            <label>Sales Agent *</label>
                            <select
                                value={assignAgentForm.sales_agent_id}
                                onChange={(e) => setAssignAgentForm((prev) => ({ ...prev, sales_agent_id: e.target.value }))}
                                className="form-input"
                                required
                            >
                                {salesAgents.map((agent) => (
                                    <option key={agent.id} value={agent.id}>
                                        {agent.name}
                                        {agent.id === 1 ? ' (Default)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Commission Type *</label>
                                <select
                                    value={assignAgentForm.sales_agent_commission_type}
                                    onChange={(e) => setAssignAgentForm((prev) => ({ ...prev, sales_agent_commission_type: e.target.value }))}
                                    className="form-input"
                                    required
                                >
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Commission Value *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={assignAgentForm.sales_agent_commission_type === 'percentage' ? '100' : undefined}
                                    value={assignAgentForm.sales_agent_commission_value}
                                    onChange={(e) => setAssignAgentForm((prev) => ({ ...prev, sales_agent_commission_value: e.target.value }))}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Start Date *</label>
                                <input
                                    type="date"
                                    value={assignAgentForm.sales_agent_start_date}
                                    onChange={(e) => setAssignAgentForm((prev) => ({ ...prev, sales_agent_start_date: e.target.value }))}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>End Date (Optional)</label>
                                <input
                                    type="date"
                                    value={assignAgentForm.sales_agent_end_date}
                                    onChange={(e) => setAssignAgentForm((prev) => ({ ...prev, sales_agent_end_date: e.target.value }))}
                                    className="form-input"
                                    min={assignAgentForm.sales_agent_start_date}
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCloseAssignAgentModal}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-success">
                                <i className="fas fa-save"></i>
                                Save Assignment
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Details Modal */}
            <Modal
                isOpen={showDetailsModal}
                onClose={handleCloseDetailsModal}
                title="Organization Details"
                size="large"
            >
                {selectedOrg && (
                    <div className="org-details-content">
                        <div className="details-grid">
                            {/* Add this section to the Details Modal */}
                            <div className="detail-section">
                                <h4>Organization Logo</h4>
                                <div className="detail-item">
                                    <label>Current Logo:</label>
                                    <span>
                                        {selectedOrg.logo ? (
                                            <img
                                                src={getImageUrl(selectedOrg.logo)}
                                                alt={selectedOrg.name}
                                                className="detail-logo-preview"
                                                style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '8px' }}
                                            />
                                        ) : (
                                            'No logo uploaded'
                                        )}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <label>Actions:</label>
                                    <span>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => {
                                                setShowDetailsModal(false);
                                                openLogoModal(selectedOrg);
                                            }}
                                        >
                                            <i className="fas fa-camera"></i>
                                            Update Logo
                                        </button>
                                    </span>
                                </div>
                            </div>
                            <div className="detail-section">
                                <h4>Basic Information</h4>
                                <div className="detail-item">
                                    <label>Name:</label>
                                    <span>{selectedOrg.name}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Country:</label>
                                    <span>{selectedOrg.country_name || selectedOrg.country_code || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Status:</label>
                                    <span>{getStatusBadge(selectedOrg.status)}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Description:</label>
                                    <span>{selectedOrg.description || 'No description provided'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Contact Information</h4>
                                <div className="detail-item">
                                    <label>Contact Email:</label>
                                    <span>{selectedOrg.contact_email}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Contact Phone:</label>
                                    <span>{selectedOrg.contact_phone || 'N/A'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Admin Information</h4>
                                <div className="detail-item">
                                    <label>Admin Username:</label>
                                    <span>{selectedOrg.admin_username || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Admin Name:</label>
                                    <span>{selectedOrg.admin_name || selectedOrg.admin_username || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Admin Email:</label>
                                    <span>{selectedOrg.admin_email || 'N/A'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Subscription & Usage</h4>
                                <div className="detail-item">
                                    <label>Actions:</label>
                                    <span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={handleExtendSubscription}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-calendar-plus"></i> Extend Subscription
                                        </button>
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <label>Subscription:</label>
                                    <span>{getSubscriptionBadge(selectedOrg.subscription_status)}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Subscription Type:</label>
                                    <span>{selectedOrg.subscription_type || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Subscription Valid Till Date:</label>
                                    <span>
                                        {selectedOrg.subscription_end_date
                                            ? orgTimestamp(selectedOrg, 'subscription_end_date', selectedOrg.timezone, true)
                                            : (selectedOrg.subscription_status === 'active' ? 'No Expiry' : 'N/A')}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <label>Staff Usage:</label>
                                    <span>{getUsedStaffCount(selectedOrg)} / {getTotalStaffCount(selectedOrg)}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Premises Count:</label>
                                    <span>{selectedOrg.premises_count || 0}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Timestamps</h4>
                                <div className="detail-item">
                                    <label>Created:</label>
                                    <span>{orgTimestamp(selectedOrg, 'created_at', selectedOrg.timezone)}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Updated:</label>
                                    <span>{orgTimestamp(selectedOrg, 'updated_at', selectedOrg.timezone)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Toast Notification */}
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />
        </div>
    );
};

export default OrganizationManagement;
